import React, { useEffect, createElement } from 'react';
import { FadeIn } from './ui/FadeIn';
import { Button } from './ui/Button';
import { Accordion } from './ui/Accordion';
import { Footer } from './Footer';
import { CheckCircle2Icon, XCircleIcon, BookOpenIcon, CoffeeIcon, ActivityIcon, UtensilsIcon, SearchIcon, ArrowDownIcon, ShieldCheckIcon, ZapIcon } from 'lucide-react';
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: (...args: any[]) => void;
  }
}
export function SalesPage() {
  // Initialize Meta Pixel
  useEffect(() => {
    if (typeof window.fbq === 'function') return;
    const n: any = window.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!window._fbq) window._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
    window.fbq('init', '2266871390506573');
    window.fbq('track', 'PageView');
  }, []);
  const handleCheckoutClick = () => {
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'InitiateCheckout');
    }
  };
  const scrollToCheckout = () => {
    const checkoutSection = document.getElementById('checkout');
    if (checkoutSection) {
      checkoutSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-navy-900 text-gray-100 font-body selection:bg-brand-orange selection:text-white" data-id="element-34">
      {/* SECTION 1 - HERO */}
      <section className="relative pt-12 pb-20 md:pt-20 md:pb-32 overflow-hidden" data-id="element-35">
        {/* Background glow effect */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[500px] bg-brand-orange/20 blur-[120px] rounded-full pointer-events-none" data-id="element-36" />

        <div className="container mx-auto px-4 relative z-10" data-id="element-37">
          <div className="max-w-4xl mx-auto text-center" data-id="element-38">
            <FadeIn data-id="element-39">
              <div className="inline-block px-4 py-1.5 rounded-full border border-brand-orange/30 bg-brand-orange/10 text-brand-light font-bold text-sm md:text-base mb-6 tracking-wide uppercase" data-id="element-40">
                The Science-Backed Protocol
              </div>
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading font-extrabold text-white leading-tight mb-6" data-id="element-41">
                The 48-Hour Glucose Reset:{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-light to-brand-orange" data-id="element-42">
                  Kill the Cravings and Reclaim Your Energy
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 leading-snug mb-10 max-w-3xl mx-auto font-medium" data-id="element-43">
                Stop the 3 PM brain fog and stabilize your energy in 2
                days—without counting a single calorie or giving up the foods
                you love.
              </p>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-44">
              <div className="relative mx-auto max-w-md md:max-w-lg mb-10 group" data-id="element-45">
                <div className="absolute -inset-1 bg-gradient-to-r from-brand-orange to-brand-light rounded-2xl blur opacity-25 group-hover:opacity-40 transition duration-1000 group-hover:duration-200" data-id="element-46"></div>
                <img src="https://cdn.magicpatterns.com/uploads/vxZ2UmE11TifGykUMVJBvx/MOCKUP.png" alt="The 48-Hour Glucose Reset Ebook Bundle" className="relative rounded-2xl shadow-2xl w-full object-cover transform transition-transform duration-500 hover:scale-[1.02]" data-id="element-47" />
              </div>
            </FadeIn>

            <FadeIn delay={0.4} data-id="element-48">
              <Button onClick={scrollToCheckout} size="xl" showArrow className="w-full md:w-auto" data-id="element-49">
                Get Instant Access Now
              </Button>
              <p className="mt-4 text-sm text-gray-400 flex items-center justify-center gap-2" data-id="element-50">
                <ShieldCheckIcon className="w-4 h-4 text-green-400" data-id="element-51" /> Secure
                Checkout • Instant Digital Access
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* SECTION 2 - OPENING HOOK */}
      <section className="py-20 bg-navy-800 border-y border-navy-700" data-id="element-52">
        <div className="container mx-auto px-4" data-id="element-53">
          <div className="max-w-3xl mx-auto" data-id="element-54">
            <FadeIn data-id="element-55">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-8 text-center" data-id="element-56">
                You know that moment at 2:45 PM.
              </h2>
              <div className="space-y-4 text-lg md:text-xl text-gray-300 leading-snug" data-id="element-57">
                <p data-id="element-58">
                  The screen goes blurry. Your head feels heavy. The only thing
                  that seems capable of saving you is a massive coffee or
                  something sweet from the breakroom.
                </p>
                <p data-id="element-59">
                  You feel like a failure for lacking "willpower," but the truth
                  is much simpler:{' '}
                  <span className="text-white font-semibold" data-id="element-60">
                    You are trapped on a biological roller coaster that sleep
                    cannot fix.
                  </span>
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* SECTION 3 - THE PROBLEM */}
      <section className="py-20 bg-navy-900" data-id="element-61">
        <div className="container mx-auto px-4" data-id="element-62">
          <div className="max-w-3xl mx-auto" data-id="element-63">
            <FadeIn data-id="element-64">
              <div className="flex items-center justify-center mb-6" data-id="element-65">
                <ActivityIcon className="w-12 h-12 text-red-500" data-id="element-66" />
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-8 text-center" data-id="element-67">
                The Danger of "Invisible Glucose Spikes"
              </h2>
              <div className="space-y-4 text-lg text-gray-300 leading-snug" data-id="element-68">
                <p data-id="element-69">
                  Most people living in the modern world are suffering from
                  "Invisible Glucose Spikes." You eat a "healthy" breakfast like
                  oatmeal or fruit yogurt, and by 11 AM, your blood sugar has
                  skyrocketed and crashed back into the dirt.
                </p>
                <p className="p-6 bg-navy-800 border-l-4 border-red-500 rounded-r-lg my-6 text-white font-medium text-xl italic" data-id="element-70">
                  This crash triggers an emergency signal in your brain: "Eat
                  sugar now or we die."
                </p>
                <p data-id="element-71">
                  This isn't a lack of character. It's a survival mechanism.
                  When you live in this cycle, you aren't just tired; you are
                  aging faster, storing fat around your midsection, and losing
                  your most productive hours to "The Fog."
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* SECTION 4 - WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="py-20 bg-white text-navy-900" data-id="element-72">
        <div className="container mx-auto px-4" data-id="element-73">
          <div className="max-w-4xl mx-auto" data-id="element-74">
            <FadeIn data-id="element-75">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-12" data-id="element-76">
                Why Everything You've Tried Has Failed
              </h2>
              <div className="grid md:grid-cols-3 gap-8" data-id="element-77">
                <div className="bg-gray-50 p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-78">
                  <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-6 font-bold text-xl" data-id="element-79">
                    1
                  </div>
                  <h3 className="font-heading font-bold text-xl mb-3" data-id="element-80">
                    Keto & Low-Carb
                  </h3>
                  <p className="text-gray-600 leading-snug" data-id="element-81">
                    Too restrictive and socially impossible to maintain
                    long-term.
                  </p>
                </div>
                <div className="bg-gray-50 p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-82">
                  <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-6 font-bold text-xl" data-id="element-83">
                    2
                  </div>
                  <h3 className="font-heading font-bold text-xl mb-3" data-id="element-84">
                    Counting Calories
                  </h3>
                  <p className="text-gray-600 leading-snug" data-id="element-85">
                    Ignores the massive hormonal impact of <em data-id="element-86">when</em> and{' '}
                    <em data-id="element-87">how</em> you eat those calories.
                  </p>
                </div>
                <div className="bg-gray-50 p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-88">
                  <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-6 font-bold text-xl" data-id="element-89">
                    3
                  </div>
                  <h3 className="font-heading font-bold text-xl mb-3" data-id="element-90">
                    Caffeine Overloading
                  </h3>
                  <p className="text-gray-600 leading-snug" data-id="element-91">
                    Only masks the problem while ruining your sleep and making
                    the next day's crash even worse.
                  </p>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* SECTION 5 - THE UNIQUE MECHANISM */}
      <section className="py-24 bg-navy-800 relative overflow-hidden" data-id="element-92">
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-brand-orange/10 blur-[100px] rounded-full pointer-events-none" data-id="element-93" />
        <div className="container mx-auto px-4 relative z-10" data-id="element-94">
          <div className="max-w-3xl mx-auto text-center" data-id="element-95">
            <FadeIn data-id="element-96">
              <h2 className="text-4xl md:text-5xl font-heading font-extrabold text-brand-light mb-8" data-id="element-97">
                Introducing: The 1-2-3 Sequencing Protocol.
              </h2>
              <div className="space-y-6 text-lg md:text-xl text-gray-300 leading-snug text-left" data-id="element-98">
                <p data-id="element-99">
                  Clinical science shows that eating the exact same meal in a
                  specific order can reduce your glucose spike by up to{' '}
                  <strong className="text-white text-2xl" data-id="element-100">75%</strong>.
                </p>
                <div className="bg-navy-900 p-6 md:p-8 rounded-2xl border border-brand-orange/30 shadow-[0_0_30px_rgba(249,115,22,0.1)]" data-id="element-101">
                  <p className="font-bold text-white mb-4 text-center" data-id="element-102">
                    The Physical Buffer:
                  </p>
                  <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-lg font-heading font-bold" data-id="element-103">
                    <span className="text-green-400" data-id="element-104">Fiber</span>
                    <ArrowDownIcon className="md:-rotate-90 text-gray-500 w-5 h-5" data-id="element-105" />
                    <span className="text-blue-400" data-id="element-106">Protein/Fat</span>
                    <ArrowDownIcon className="md:-rotate-90 text-gray-500 w-5 h-5" data-id="element-107" />
                    <span className="text-brand-light" data-id="element-108">Starches</span>
                  </div>
                </div>
                <p data-id="element-109">
                  By layering your food in this exact order, you create a
                  physical "buffer" in your gut that slows down sugar
                  absorption.{' '}
                  <strong className="text-white" data-id="element-110">
                    You get the energy of the carbs without the devastating
                    crash that follows.
                  </strong>
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* SECTION 6 - WHAT YOU GET */}
      <section className="py-20 bg-navy-900" data-id="element-111">
        <div className="container mx-auto px-4" data-id="element-112">
          <div className="max-w-4xl mx-auto" data-id="element-113">
            <FadeIn data-id="element-114">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-12" data-id="element-115">
                Inside The 48-Hour Glucose Reset Bundle
              </h2>
              <div className="grid gap-4" data-id="element-116">
                {[{
                icon: <BookOpenIcon className="w-6 h-6 text-brand-orange" data-id="element-117" />,
                title: 'The 48-Hour Rapid Reset Calendar',
                desc: 'A step-by-step 2-day plan to flatten your curve and kill cravings immediately.'
              }, {
                icon: <CoffeeIcon className="w-6 h-6 text-brand-orange" data-id="element-118" />,
                title: 'The Savory Start Guide',
                desc: 'How to anchor your morning so you stay full and focused until 2:00 PM.'
              }, {
                icon: <ZapIcon className="w-6 h-6 text-brand-orange" data-id="element-119" />,
                title: 'The 10-Minute Kinetic Buffer',
                desc: "Simple movements that turn your muscles into 'glucose sponges' after a big meal."
              }, {
                icon: <UtensilsIcon className="w-6 h-6 text-brand-orange" data-id="element-120" />,
                title: "The 'Eating Out' Cheat Sheet",
                desc: 'How to navigate any restaurant menu without spiking your sugar.'
              }, {
                icon: <SearchIcon className="w-6 h-6 text-brand-orange" data-id="element-121" />,
                title: 'Hidden Sugar Audit',
                desc: "A checklist to find the secret energy-killers hiding in your 'health' foods."
              }].map((item, i) => <div key={i} className="flex items-start gap-4 p-6 bg-navy-800 rounded-xl border border-navy-700 hover:border-brand-orange/50 transition-colors" data-id="element-122">
                    <div className="flex-shrink-0 mt-1 bg-navy-900 p-3 rounded-lg border border-navy-700" data-id="element-123">
                      {item.icon}
                    </div>
                    <div data-id="element-124">
                      <h3 className="font-heading font-bold text-xl text-white mb-1" data-id="element-125">
                        {item.title}
                      </h3>
                      <p className="text-gray-400 leading-snug" data-id="element-126">{item.desc}</p>
                    </div>
                  </div>)}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* SECTION 7 - WHO THIS IS FOR / NOT FOR */}
      <section className="py-20 bg-white text-navy-900" data-id="element-127">
        <div className="container mx-auto px-4" data-id="element-128">
          <div className="max-w-5xl mx-auto" data-id="element-129">
            <FadeIn data-id="element-130">
              <div className="grid md:grid-cols-2 gap-8 md:gap-12" data-id="element-131">
                {/* FOR */}
                <div className="bg-gray-50 p-8 rounded-2xl border border-gray-200" data-id="element-132">
                  <h3 className="text-2xl font-heading font-bold mb-6 flex items-center gap-3" data-id="element-133">
                    <CheckCircle2Icon className="w-8 h-8 text-green-500" data-id="element-134" />
                    Who This Is For
                  </h3>
                  <ul className="space-y-4" data-id="element-135">
                    {['Overworked professionals who lose their focus every afternoon.', 'Parents who are too exhausted to play with their kids after 5 PM.', "Anyone tired of 'dieting' but wanting to feel better in their own skin.", 'People who want to enjoy bread, pasta, or fruit without the guilt or the fog.'].map((text, i) => <li key={i} className="flex items-start gap-3" data-id="element-136">
                        <CheckCircle2Icon className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" data-id="element-137" />
                        <span className="text-gray-700 leading-snug font-medium" data-id="element-138">
                          {text}
                        </span>
                      </li>)}
                  </ul>
                </div>

                {/* NOT FOR */}
                <div className="bg-red-50/50 p-8 rounded-2xl border border-red-100" data-id="element-139">
                  <h3 className="text-2xl font-heading font-bold mb-6 flex items-center gap-3" data-id="element-140">
                    <XCircleIcon className="w-8 h-8 text-red-500" data-id="element-141" />
                    Who This Is NOT For
                  </h3>
                  <ul className="space-y-4" data-id="element-142">
                    {['People seeking medical treatment for Type 1 Diabetes (consult your doctor).', "Those looking for a 'magic pill' that requires zero change in habits.", 'People who enjoy the feeling of being tired and irritable.'].map((text, i) => <li key={i} className="flex items-start gap-3" data-id="element-143">
                        <XCircleIcon className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" data-id="element-144" />
                        <span className="text-gray-600 leading-snug" data-id="element-145">
                          {text}
                        </span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* SECTION 8 - HOW IT WORKS */}
      <section className="py-20 bg-navy-800" data-id="element-146">
        <div className="container mx-auto px-4" data-id="element-147">
          <div className="max-w-4xl mx-auto" data-id="element-148">
            <FadeIn data-id="element-149">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-16" data-id="element-150">
                How It Works
              </h2>
              <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-151">
                {/* Connecting line for desktop */}
                <div className="hidden md:block absolute top-12 left-[15%] right-[15%] h-0.5 bg-navy-600 z-0" data-id="element-152"></div>

                {[{
                step: '1',
                title: 'Download the PDF',
                desc: 'Get instant access to the protocol and checklists.'
              }, {
                step: '2',
                title: 'Apply the Sequence',
                desc: 'Use the 1-2-3 Rule at your very next meal.'
              }, {
                step: '3',
                title: 'Feel the Quiet',
                desc: 'Watch your cravings disappear and your energy stabilize within 48 hours.'
              }].map((item, i) => <div key={i} className="relative z-10 flex flex-col items-center text-center" data-id="element-153">
                    <div className="w-24 h-24 bg-navy-900 border-4 border-brand-orange rounded-full flex items-center justify-center text-3xl font-heading font-bold text-white mb-6 shadow-[0_0_20px_rgba(249,115,22,0.2)]" data-id="element-154">
                      {item.step}
                    </div>
                    <h3 className="text-xl font-heading font-bold text-white mb-3" data-id="element-155">
                      {item.title}
                    </h3>
                    <p className="text-gray-400 leading-snug" data-id="element-156">{item.desc}</p>
                  </div>)}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* SECTION 9 - PROOF / BELIEVABILITY */}
      <section className="py-20 bg-brand-orange text-navy-900" data-id="element-157">
        <div className="container mx-auto px-4" data-id="element-158">
          <div className="max-w-3xl mx-auto text-center" data-id="element-159">
            <FadeIn data-id="element-160">
              <h2 className="text-3xl md:text-4xl font-heading font-extrabold mb-8" data-id="element-161">
                The 7-Day Snapshot
              </h2>
              <div className="bg-white/90 backdrop-blur-sm p-8 md:p-10 rounded-2xl shadow-xl text-left space-y-6 text-lg md:text-xl font-medium leading-snug" data-id="element-162">
                <p className="flex items-start gap-4" data-id="element-163">
                  <span className="font-heading font-bold text-brand-orange text-2xl w-32 flex-shrink-0" data-id="element-164">
                    Within 48h:
                  </span>
                  <span data-id="element-165">
                    The "noise" of sugar cravings typically goes completely
                    silent.
                  </span>
                </p>
                <div className="h-px bg-gray-200 w-full" data-id="element-166"></div>
                <p className="flex items-start gap-4" data-id="element-167">
                  <span className="font-heading font-bold text-brand-orange text-2xl w-32 flex-shrink-0" data-id="element-168">
                    By Day 7:
                  </span>
                  <span data-id="element-169">
                    Most users report waking up before their alarm and no longer
                    needing a nap or a caffeine fix after lunch.
                  </span>
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* SECTION 10 - FAQ */}
      <section className="py-20 bg-navy-900" data-id="element-170">
        <div className="container mx-auto px-4" data-id="element-171">
          <div className="max-w-3xl mx-auto" data-id="element-172">
            <FadeIn data-id="element-173">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-12" data-id="element-174">
                Frequently Asked Questions
              </h2>
              <Accordion items={[{
              question: 'Do I have to stop eating carbs?',
              answer: "No. You just learn the correct order to eat them so they don't hurt your energy."
            }, {
              question: 'How long does it take to see results?',
              answer: 'Most people feel a significant change in mental clarity within the first 2 days.'
            }, {
              question: 'Is this a book or a physical product?',
              answer: 'This is a digital PDF bundle designed for immediate use on your phone or laptop.'
            }, {
              question: 'Do I need expensive supplements?',
              answer: 'No. This protocol relies entirely on real food and simple timing.'
            }]} data-id="element-175" />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* SECTION 11 - OFFER + PRICE BLOCK (CHECKOUT) */}
      <section id="checkout" className="py-24 bg-navy-800 border-t border-navy-700 scroll-mt-10" data-id="element-176">
        <div className="container mx-auto px-4" data-id="element-177">
          <div className="max-w-2xl mx-auto" data-id="element-178">
            <FadeIn data-id="element-179">
              <div className="bg-navy-900 border-2 border-brand-orange rounded-3xl p-8 md:p-12 text-center shadow-[0_0_50px_rgba(249,115,22,0.15)] relative overflow-hidden" data-id="element-180">
                {/* Decorative corner accent */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-brand-orange/20 blur-[50px] rounded-full" data-id="element-181"></div>

                <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6 relative z-10" data-id="element-182">
                  Reclaim your focus and end the sugar cycle today.
                </h2>
                <p className="text-lg text-gray-300 mb-10 leading-snug relative z-10" data-id="element-183">
                  For the cost of a few lattes, you can have a lifelong
                  framework for stable energy.
                </p>

                <div className="bg-navy-800 rounded-2xl p-6 mb-8 border border-navy-700 relative z-10" data-id="element-184">
                  <h3 className="font-heading font-bold text-xl text-white mb-4" data-id="element-185">
                    The 48-Hour Glucose Reset Bundle
                  </h3>
                  <div className="flex items-center justify-center gap-4 mb-2" data-id="element-186">
                    <span className="text-2xl text-gray-500 line-through font-medium" data-id="element-187">
                      $27.00
                    </span>
                    <span className="text-5xl md:text-6xl font-heading font-extrabold text-brand-light" data-id="element-188">
                      $6.36{' '}
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-3 mt-3" data-id="element-189">
                    <span className="inline-block bg-green-500/20 text-green-400 font-extrabold text-lg px-4 py-1.5 rounded-full border border-green-500/30" data-id="element-190">
                      76% OFF
                    </span>
                  </div>
                  <p className="text-green-400 font-bold text-sm uppercase tracking-wider mt-3" data-id="element-191">
                    Special Discount Applied
                  </p>
                </div>

                <Button size="xl" fullWidth showArrow href="https://plenava.com/cart/52535921443163id1:1" onClick={handleCheckoutClick} className="relative z-10 mb-6 text-xl" data-id="element-192">
                  Get Instant Access
                </Button>

                <div className="flex flex-col items-center justify-center gap-3 text-sm text-gray-400 relative z-10" data-id="element-193">
                  <div className="flex items-center gap-2" data-id="element-194">
                    <ShieldCheckIcon className="w-5 h-5 text-gray-500" data-id="element-195" />
                    <span data-id="element-196">Secure encrypted checkout</span>
                  </div>
                  <p data-id="element-197">Instant digital download. Access on any device.</p>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <Footer data-id="element-198" />
    </div>;
}